function parseMaybeNumber(x) {
  if (x == null) return null;
  if (typeof x === "number" && Number.isFinite(x)) return x;
  if (typeof x !== "string") return null;
  const cleaned = x.replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function getPathValue(obj, path) {
  if (!obj) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

function pickNumberFromPaths(obj, paths) {
  for (const p of paths) {
    const v = getPathValue(obj, p);
    const n = parseMaybeNumber(v);
    if (n != null) return n;
  }
  return null;
}

function safeNumber(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

export function getCampaignOutcomeCounts(summary) {
  const s = summary || {};
  const recipients =
    pickNumberFromPaths(s, [
      "recipients",
      "totalRecipients",
      "totalSent",
      "stats.recipients",
      "data.totalRecipients",
      "data.totalSent",
    ]) ?? 0;
  const sent = pickNumberFromPaths(s, ["sent", "stats.sent", "data.sent"]) ?? 0;
  const delivered =
    pickNumberFromPaths(s, ["delivered", "stats.delivered", "data.delivered"]) ?? 0;
  const read = pickNumberFromPaths(s, ["read", "stats.read", "data.read"]) ?? 0;
  const urlClicked =
    pickNumberFromPaths(s, [
      "urlClickedCount",
      "urlClicked",
      "clickedCount",
      "clicked",
      "stats.urlClickedCount",
      "stats.urlClicked",
      "stats.clicked",
      "data.urlClickedCount",
      "data.urlClicked",
      "data.clicked",
    ]) ?? 0;
  const buttonClickedTrackingAvailable =
    s.buttonClickedTrackingAvailable ??
    s.stats?.buttonClickedTrackingAvailable ??
    s.data?.buttonClickedTrackingAvailable ??
    false;
  // The server owns the URL/button union: the separate counts can overlap.
  // Older responses retain their legacy click count; never sum overlapping counters.
  const clicks = pickNumberFromPaths(s, ["clicks", "stats.clicks", "data.clicks"]) ?? urlClicked;
  const rawButtonClicked =
    pickNumberFromPaths(s, [
      "buttonClickedCount",
      "buttonClicked",
      "stats.buttonClickedCount",
      "stats.buttonClicked",
      "data.buttonClickedCount",
      "data.buttonClicked",
    ]);
  const replied =
    pickNumberFromPaths(s, [
      "repliedCount",
      "replied",
      "repliedWithinWindow",
      "stats.repliedCount",
      "stats.repliedWithinWindow",
      "stats.replied",
      "data.repliedCount",
      "data.replied",
    ]) ?? 0;
  const failed =
    pickNumberFromPaths(s, ["failed", "stats.failed", "data.failed"]) ?? 0;

  const total =
    recipients ||
    Math.max(
      safeNumber(sent),
      safeNumber(delivered),
      safeNumber(read),
      safeNumber(urlClicked),
      safeNumber(rawButtonClicked),
      safeNumber(replied),
      safeNumber(failed)
    );

  return {
    recipients: safeNumber(total),
    sent: safeNumber(sent),
    delivered: safeNumber(delivered),
    // Delivered already includes Read. All three outcomes use the same message population.
    awaitingDelivery: Math.max(0, safeNumber(total) - safeNumber(delivered) - safeNumber(failed)),
    read: safeNumber(read),
    clicked: safeNumber(urlClicked),
    clicks: safeNumber(clicks),
    urlClicked: safeNumber(urlClicked),
    buttonClicked:
      buttonClickedTrackingAvailable || rawButtonClicked != null
        ? safeNumber(rawButtonClicked)
        : null,
    buttonClickedTrackingAvailable: Boolean(buttonClickedTrackingAvailable || rawButtonClicked != null),
    replied: safeNumber(replied),
    failed: safeNumber(failed),
  };
}

export function extractCampaignAudienceTotalCount(raw) {
  if (!raw) return null;
  const n =
    raw.totalCount ??
    raw.TotalCount ??
    raw?.meta?.totalCount ??
    raw?.Meta?.TotalCount ??
    raw?.pagination?.totalCount ??
    raw?.Pagination?.TotalCount ??
    null;
  if (n == null) return null;
  return Number.isFinite(Number(n)) ? Number(n) : null;
}
